<!--
 * @Description: 账户管理首页
 * @Date: 2025-05-19 18:01:07
-->
<template>
  <div class="account-main box-flex-column">
    <div class="account-main__top" v-xrk-loading="!banks.length">
      <div class="account-main__account">
        <div class="account-main__label">账户余额（元）</div>
        <div v-resource="['支付通道']" class="account-main__banks">
          <template v-for="item in banks" :key="item.label">
            <div class="account-main__banks__item" v-if="item.enabled">
              <span class="account-main__banks__label">
                {{ item.label }}
              </span>

              <BaseButton link type="primary" @click="onOpenBank(item)">
                <div style="display: flex; align-items: center">
                  <span style="margin-right: 6px">
                    {{ OPEN_STATUS_TEXT[item.actOpenStatus] }}
                  </span>
                  <BaseIcon size="12">
                    <ArrowRightBold />
                  </BaseIcon>
                </div>
              </BaseButton>
            </div>
          </template>

          <div class="account-main__banks__line"></div>

          <BaseButton
            link
            type="default"
            @click="onOnlineConfig"
            v-if="IS_PLATFORM_TYPE_1"
          >
            <div style="display: flex; align-items: center">
              <span style="margin-right: 6px">网银支付</span>
              <BaseIcon size="12">
                <ArrowRightBold />
              </BaseIcon>
            </div>
          </BaseButton>
        </div>
      </div>
      <div class="account-main__money">
        <div class="account-mainmain__money__num">
          {{ formatter.fen2yuan(companyInfo.balance) }}
        </div>

        <div class="account-mainmain__money__btn">
          <BaseButton
            v-resource="['提现']"
            v-if="bankApproved && companyInfo.amount"
            style="margin-top: 24px"
            @click="onWithdrawal"
          >
            提现
          </BaseButton>

          <BaseButton
            v-resource="['充值']"
            v-if="bankApproved"
            style="margin-top: 24px"
            type="default"
            @click="onRecharge"
          >
            充值
          </BaseButton>
        </div>
      </div>
    </div>

    <BaseLayoutFlexContent>
      <AccountMainList ref="accountMainListRef" />
    </BaseLayoutFlexContent>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue';
import {
  BaseButton,
  useCreateModal,
  BaseIcon,
  XrkMessage,
  BaseLayoutFlexContent
} from 'xrk-components';
import AccountMainList from '../components/account-main-list.vue';
import AccountMainWithdrawalDialog from '../components/account-main-withdrawal-dialog.vue';
import AccountMainRechargeDialog from '../components/account-main-recharge-dialog.vue';
import { getEnterpriseAccountBalance } from '../data-source/apis';
import { GetEnterpriseAccountBalanceData } from '../data-source/types';
import { useBank } from '../hooks/index';
import { pushfyDetail } from '../router';
import { formatter } from '@/utils';
// import { FunctionCode } from '@cTypes/user';
import AccountMainWithdrawalOrdersDialog from '../components/account-main-withdrawal-orders-dialog.vue';
import { useRouteMeta } from '@hooks/common';
import { ArrowRightBold } from '@element-plus/icons-vue';
import AccountOnlineConfigDialog from '../components/account-online-config-dialog.vue';
import { IS_PLATFORM_TYPE_1 } from '@/config';

const OPEN_STATUS_TEXT = {
  10: '开户申请',
  20: '开通中',
  30: '已开通',
  40: '开通失败'
};

const { banks, updateBanks, openInfo } = useBank();
const routeMeta = useRouteMeta();
const isAsp = [2].includes(+routeMeta.functionCode);
const isSupplier = [3].includes(+routeMeta.functionCode);
const isDbgys = [8].includes(+routeMeta.functionCode);

const accountMainListRef = ref();
const companyInfo = ref<GetEnterpriseAccountBalanceData>({
  balance: 0,
  amount: 0,
  waitAmount: 0
});

const bankApproved = computed(() => {
  const t = banks.value.find(
    item => item.value === 'fyBank' && item.actOpenStatus === 30
  );
  // 只有富友可以提现、充值
  return t;
});

const setCompany = async () => {
  const { data } = await getEnterpriseAccountBalance();
  companyInfo.value = data;
};
const init = async () => {
  updateBanks();
  setCompany();
};

const onOpenBank = (item: (typeof banks.value)[0]) => {
  if (!item.enabled) return; // 未启用不可进入
  pushfyDetail({
    payPassagewayCode: 'fyBank'
  });

  // useCreateModal(AccountInfoDialog, {
  //   payPassagewayCode: item.payPassagewayCode,
  //   onSuccess() {
  //     init();
  //     accountMainListRef.value.init();
  //   }
  // });
};

const onRecharge = () => {
  useCreateModal(AccountMainRechargeDialog);
};
const onWithdrawal = () => {
  if (isAsp || isDbgys) {
    useCreateModal(AccountMainWithdrawalDialog, {
      onSuccess() {
        init();
      }
    });
  }
  if (isSupplier) {
    useCreateModal(AccountMainWithdrawalOrdersDialog, {
      onSuccess() {
        init();
      }
    });
  }
};
const onOnlineConfig = () => {
  if (!openInfo.value.deptId) {
    XrkMessage.error('数据异常');
    return;
  }
  useCreateModal(AccountOnlineConfigDialog, {
    deptId: openInfo.value.deptId,
    entpName: openInfo.value.entpName,
    onSuccess() {
      init();
    }
  });
};

onMounted(() => {
  init();
});
</script>
<style lang="scss" scoped>
.account-main__top {
  margin-bottom: 24px;
  .account-main__account {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;

    .account-main__label {
      font-weight: 600;
      font-size: 16px;
      color: #121619;
      line-height: 24px;
      text-align: left;
      font-style: normal;
      text-transform: none;
    }

    .account-main__banks {
      display: flex;
      align-items: center;

      .account-main__banks__item {
        display: flex;
        align-items: center;
        .account-main__banks__label {
          margin-right: 10px;
          font-weight: 400;
          font-size: 14px;
          color: #121619;
          line-height: 22px;
          text-align: right;
          font-style: normal;
          text-transform: none;
        }
      }

      .account-main__banks__line {
        margin: 0 16px;
        width: 1px;
        height: 12px;
        background: #c1c7cd;
      }
    }
  }
  .account-main__money {
    border: 1px solid #dde1e6;
    border-radius: 4px;
    box-sizing: border-box;
    padding: 16px;

    .account-mainmain__money__num {
      font-weight: 600;
      font-size: 24px;
      color: var(--xrk-color-primary);
      line-height: 32px;
      text-align: left;
      font-style: normal;
      text-transform: none;
    }
  }
}
</style>
